import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { ArrowUpRight, Banknote, TrendingDown, TrendingUp, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { money, shortDate, ugx } from "@/lib/format";
import { Panel, StatusPill, Empty } from "@/components/Shell";

const KPI = ({ label, value, icon: Icon, tone = "slate", hint }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-5 transition-transform hover:-translate-y-px" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <div className="flex items-start justify-between">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</p>
      <Icon size={18} className={tone === "emerald" ? "text-emerald-600" : tone === "rose" ? "text-rose-500" : "text-slate-400"} strokeWidth={1.8} />
    </div>
    <p className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900">UGX {money(value)}</p>
    {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch(() => setData(false));
  }, []);

  if (!data) return <p className="text-sm text-slate-500">Loading your books…</p>;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Company overview</h2>
        <p className="mt-1 text-sm text-slate-500">Year-to-date performance in Ugandan Shillings.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <KPI label="Cash & Bank" value={data.cash_balance} icon={Banknote} tone="emerald" hint="Cash, bank and mobile money" />
        <KPI label="Receivables" value={data.receivables} icon={TrendingUp} hint={`${data.unpaid_invoices} unpaid invoices`} />
        <KPI label="Payables" value={data.payables} icon={TrendingDown} tone="rose" hint={`${data.bill_count} bills recorded`} />
        <KPI label="Net Profit YTD" value={data.net_profit_ytd} icon={ArrowUpRight} tone="emerald" hint={`Income ${ugx(data.income_ytd)}`} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Panel title="Income vs Expenses (6 months)" className="lg:col-span-2">
          <div className="h-72 p-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.series}>
                <defs>
                  <linearGradient id="inc" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#059669" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#059669" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="exp" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0EA5E9" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#0EA5E9" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="month" stroke="#94A3B8" fontSize={12} />
                <YAxis stroke="#94A3B8" fontSize={12} tickFormatter={(v) => money(v / 1000) + "k"} />
                <Tooltip formatter={(v) => ugx(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff" }} />
                <Area type="monotone" dataKey="income" stroke="#059669" fill="url(#inc)" strokeWidth={2} />
                <Area type="monotone" dataKey="expenses" stroke="#0EA5E9" fill="url(#exp)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Top expense accounts">
          <div className="h-72 p-4">
            {data.top_expenses?.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.top_expenses} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                  <XAxis type="number" stroke="#94A3B8" fontSize={11} tickFormatter={(v) => money(v / 1000) + "k"} />
                  <YAxis type="category" dataKey="name" stroke="#94A3B8" fontSize={11} width={90} />
                  <Tooltip formatter={(v) => ugx(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0" }} />
                  <Bar dataKey="amount" fill="#0F172A" radius={[0, 4, 4, 0]} barSize={16} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Empty message="No expenses recorded yet." />
            )}
          </div>
        </Panel>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Panel
          title="Recent invoices"
          className="lg:col-span-2"
          right={<Link to="/invoices" data-testid="dashboard-view-invoices" className="text-xs font-semibold text-emerald-700 hover:underline">View all</Link>}
        >
          {data.recent_invoices?.length ? (
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-5 py-2.5">Number</th>
                  <th className="px-5 py-2.5">Date</th>
                  <th className="px-5 py-2.5 text-right">Total</th>
                  <th className="px-5 py-2.5 text-right">Due</th>
                  <th className="px-5 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_invoices.map((i) => (
                  <tr key={i.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-5 py-3 font-semibold text-slate-900">{i.number}</td>
                    <td className="px-5 py-3 text-slate-600">{shortDate(i.date)}</td>
                    <td className="px-5 py-3 text-right tabular-nums">{money(i.total)}</td>
                    <td className="px-5 py-3 text-right tabular-nums">{money(i.amount_due)}</td>
                    <td className="px-5 py-3"><StatusPill status={i.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Empty message="No invoices yet. Create your first invoice to get started." />
          )}
        </Panel>

        <Panel title="Alerts">
          <div className="space-y-3 p-5">
            <div className="rounded-md border border-amber-200 bg-amber-50 p-4">
              <p className="flex items-center gap-2 text-sm font-semibold text-amber-800">
                <AlertTriangle size={16} /> Overdue receivables
              </p>
              <p className="mt-1 font-display text-xl font-bold text-amber-900">{ugx(data.overdue_receivables)}</p>
            </div>
            <div className="rounded-md border border-slate-200 p-4">
              <p className="text-sm font-semibold text-slate-800">Low stock items</p>
              {data.low_stock?.length ? (
                <ul className="mt-2 space-y-1 text-sm text-slate-600">
                  {data.low_stock.map((p) => (
                    <li key={p.id} className="flex justify-between">
                      <span>{p.name}</span>
                      <span className="tabular-nums font-medium text-rose-600">{p.quantity}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-1 text-sm text-slate-500">All stock levels are healthy.</p>
              )}
            </div>
          </div>
        </Panel>
      </div>
    </div>
  );
}
